import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api-client';
import { useToast, getErrorMessage } from '../hooks/use-toast';
import { ToastContainer } from '../components/toast';

type MemberStatus = 'all' | 'active' | 'pending' | 'suspended' | 'disabled';

interface Member {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  treatyNumber: string;
  status: string;
  createdAt: string;
}

export function MembersPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<MemberStatus>('all');
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [actionMenu, setActionMenu] = useState<string | null>(null);
  const [photoIdModal, setPhotoIdModal] = useState<{ memberId: string; url: string } | null>(null);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [reasonModalData, setReasonModalData] = useState<{
    memberId: string;
    status: string;
  } | null>(null);
  const [reasonText, setReasonText] = useState('');

  const queryClient = useQueryClient();

  // Toast notifications
  const { toasts, showToast, removeToast } = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ['members', page, statusFilter === 'all' ? undefined : statusFilter, search],
    queryFn: () =>
      adminApi.getMembers(
        page,
        20,
        statusFilter === 'all' ? undefined : statusFilter,
        search || undefined,
      ),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({
      memberId,
      status,
      reason,
    }: {
      memberId: string;
      status: string;
      reason?: string;
    }) => adminApi.updateMemberStatus(memberId, status, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      setActionMenu(null);
      setSelectedMember(null);
      showToast('Member status updated successfully', 'success');
    },
    onError: (error: unknown) => {
      showToast(getErrorMessage(error), 'error');
    },
  });

  const handleStatusChange = (memberId: string, newStatus: string) => {
    if (newStatus !== 'active') {
      setReasonModalData({ memberId, status: newStatus });
      setShowReasonModal(true);
    } else {
      if (confirm(`Change status to ${newStatus}?`)) {
        updateStatusMutation.mutate({ memberId, status: newStatus });
      }
    }
  };

  const submitStatusChange = () => {
    if (!reasonModalData || !reasonText.trim()) return;
    updateStatusMutation.mutate({
      memberId: reasonModalData.memberId,
      status: reasonModalData.status,
      reason: reasonText,
    });
    setShowReasonModal(false);
    setReasonText('');
    setReasonModalData(null);
  };

  const handleViewPhotoId = async (memberId: string) => {
    try {
      const { url } = await adminApi.getMemberPhotoIdUrl(memberId);
      setPhotoIdModal({ memberId, url });
    } catch (error) {
      showToast(getErrorMessage(error), 'error');
    }
  };

  const handleExport = async () => {
    try {
      const allData = await adminApi.getMembers(
        1,
        10000,
        statusFilter === 'all' ? undefined : statusFilter,
        search || undefined,
      );

      const csv = [
        ['Name', 'Email', 'Phone', 'Treaty Number', 'Status', 'Registered'].join(','),
        ...allData.data.map((m: Member) =>
          [
            `"${m.firstName} ${m.lastName}"`,
            m.email,
            m.phone || '',
            m.treatyNumber,
            m.status,
            new Date(m.createdAt).toLocaleDateString(),
          ].join(','),
        ),
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `members-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('Member directory exported successfully', 'success');
    } catch (error) {
      showToast(getErrorMessage(error), 'error');
    }
  };

  const statusTabs: { value: MemberStatus; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'pending', label: 'Pending' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'disabled', label: 'Disabled' },
  ];

  const filteredMembers = data?.data || [];
  const totalFiltered = data?.pagination?.total || 0;
  const startIndex = (page - 1) * 20;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#4b607f]/20 border-t-[#4b607f] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-[1100px]">
      {/* Page Header */}
      <div className="mb-8">
        <span className="block text-[10px] tracking-[1.2px] text-[#68716d] mb-[9px] uppercase font-semibold">
          Opaskwayak Cree Nation
        </span>
        <h1 className="text-[32px] font-semibold text-[#424a47] leading-[1.2] tracking-[-0.5px] mb-[9px]">
          Member directory
        </h1>
        <p className="text-[14px] text-[#68716d] leading-[1.6] mb-6">
          Find and manage your community's member records.
        </p>
        <button
          onClick={handleExport}
          className="px-4 py-[10px] bg-[#242728] text-white rounded-[9px] text-[13px] font-semibold hover:bg-[#3c413d] active:scale-[0.98] transition-all inline-flex items-center gap-2"
        >
          <svg
            className="w-[16px] h-[16px]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.65}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
          Export directory
        </button>
      </div>

      {/* Card with Search, Filters, Tabs */}
      <div className="bg-white rounded-[13px] shadow-[0_2px_8px_rgba(0,0,0,0.06)] overflow-hidden mb-6">
        {/* Search and Reset */}
        <div className="p-6 pb-0 flex gap-3 items-center">
          <div className="flex-1 relative">
            <svg
              className="absolute left-[13px] top-1/2 -translate-y-1/2 w-[16px] h-[16px] text-[#68716d] pointer-events-none"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.65}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search name, email or treaty number"
              className="w-full pl-[41px] pr-4 py-[10px] text-[13px] border border-[#e1e6d8] rounded-[9px] bg-white text-[#424a47] placeholder:text-[#68716d] focus:outline focus:outline-2 focus:outline-[#aeb6bf] focus:outline-offset-2"
            />
          </div>
          {(search || statusFilter !== 'all') && (
            <button
              onClick={() => {
                setSearch('');
                setStatusFilter('all');
                setPage(1);
              }}
              className="px-4 py-[10px] border border-[#e6e9de] rounded-[9px] hover:bg-[#f6f6f3] transition-colors text-[13px] font-medium text-[#424a47]"
            >
              Reset filters
            </button>
          )}
        </div>

        {/* Status Tabs */}
        <div className="flex gap-1 px-4 border-b border-[#e5e4df] mt-5">
          {statusTabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => {
                setStatusFilter(tab.value);
                setPage(1);
              }}
              className={`px-4 py-[11px] font-medium text-[13px] transition-colors relative ${
                statusFilter === tab.value
                  ? 'text-[#424a47]'
                  : 'text-[#68716d] hover:text-[#424a47]'
              }`}
            >
              {tab.label}
              {statusFilter === tab.value && (
                <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#4b607f]" />
              )}
            </button>
          ))}
        </div>

        {/* Members Table */}
        {filteredMembers.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-[64px] h-[64px] mx-auto mb-4 rounded-full bg-[#f6f6f3] flex items-center justify-center">
              <svg
                className="w-[32px] h-[32px] text-[#68716d]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.65}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <h3 className="text-[18px] font-semibold text-[#424a47] mb-2">No members found</h3>
            <p className="text-[14px] text-[#68716d] mb-4">
              Try another search or clear your filters.
            </p>
            <button
              onClick={() => {
                setSearch('');
                setStatusFilter('all');
                setPage(1);
              }}
              className="px-4 py-[10px] bg-[#242728] text-white rounded-[9px] text-[13px] font-semibold hover:bg-[#3c413d] active:scale-[0.98] transition-all"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#f6f6f3] border-b border-[#e5e4df]">
                  <tr>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[#68716d] uppercase tracking-[0.5px]">
                      Member
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[#68716d] uppercase tracking-[0.5px]">
                      Treaty number
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[#68716d] uppercase tracking-[0.5px]">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[#68716d] uppercase tracking-[0.5px]">
                      Registered
                    </th>
                    <th className="px-4 py-3 text-right text-[11px] font-medium text-[#68716d] uppercase tracking-[0.5px]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e5e4df]">
                  {filteredMembers.map((member: Member) => (
                    <tr key={member.id} className="hover:bg-[#f6f6f3] transition-colors">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-[36px] h-[36px] rounded-full bg-[#e1e5eb] text-[#4b607f] flex items-center justify-center font-medium text-[12px] flex-shrink-0">
                            {member.firstName[0]}
                            {member.lastName[0]}
                          </div>
                          <div>
                            <div className="font-medium text-[#424a47] text-[14px]">
                              {member.firstName} {member.lastName}
                            </div>
                            <div className="text-[12px] text-[#68716d]">{member.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-[13px] text-[#68716d] font-mono">
                        {member.treatyNumber}
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`px-[11px] py-[5px] rounded-full text-[11px] font-medium capitalize border ${
                            member.status === 'active'
                              ? 'bg-[#e1e5eb] text-[#4b607f] border-[#d1d8e3]'
                              : member.status === 'pending'
                                ? 'bg-[#fef8e6] text-[#b8942c] border-[#f4e6b8]'
                                : member.status === 'suspended'
                                  ? 'bg-[#fff1e6] text-[#f3701e] border-[#f0d8c1]'
                                  : 'bg-[#f6f6f3] text-[#68716d] border-[#e5e4df]'
                          }`}
                        >
                          {member.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-[13px] text-[#68716d]">
                        {new Date(member.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </td>
                      <td className="px-4 py-4 text-right relative">
                        <button
                          onClick={() => setActionMenu(actionMenu === member.id ? null : member.id)}
                          className="px-[9px] py-[7px] border-0 bg-transparent hover:bg-[#f6f6f3] rounded-[7px] transition-colors"
                          aria-label="Member actions"
                        >
                          <svg
                            className="w-[18px] h-[18px] text-[#68716d]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={1.65}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                            />
                          </svg>
                        </button>

                        {actionMenu === member.id && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setActionMenu(null)}
                            />
                            <div className="absolute right-4 top-full mt-1 bg-white rounded-[9px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[#e5e4df] py-2 min-w-[160px] z-20">
                              <button
                                onClick={() => {
                                  setSelectedMember(member);
                                  setActionMenu(null);
                                }}
                                className="w-full px-4 py-[9px] text-left text-[13px] text-[#424a47] hover:bg-[#f6f6f3] transition-colors"
                              >
                                View details
                              </button>
                              <button
                                onClick={() => {
                                  handleViewPhotoId(member.id);
                                  setActionMenu(null);
                                }}
                                className="w-full px-4 py-[9px] text-left text-[13px] text-[#424a47] hover:bg-[#f6f6f3] transition-colors"
                              >
                                View photo ID
                              </button>
                              <div className="my-1 border-t border-[#e5e4df]" />
                              {member.status === 'active' && (
                                <>
                                  <button
                                    onClick={() => {
                                      handleStatusChange(member.id, 'suspended');
                                      setActionMenu(null);
                                    }}
                                    className="w-full px-4 py-[9px] text-left text-[13px] text-[#f3701e] hover:bg-[#fff1e6] transition-colors"
                                  >
                                    Suspend account
                                  </button>
                                  <button
                                    onClick={() => {
                                      handleStatusChange(member.id, 'disabled');
                                      setActionMenu(null);
                                    }}
                                    className="w-full px-4 py-[9px] text-left text-[13px] text-[#c5523f] hover:bg-[#fbe9e6] transition-colors"
                                  >
                                    Disable account
                                  </button>
                                </>
                              )}
                              {(member.status === 'suspended' || member.status === 'disabled') && (
                                <button
                                  onClick={() => {
                                    handleStatusChange(member.id, 'active');
                                    setActionMenu(null);
                                  }}
                                  className="w-full px-4 py-[9px] text-left text-[13px] text-[#4b607f] hover:bg-[#e1e5eb] transition-colors"
                                >
                                  Reactivate account
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Footer */}
            <div className="flex items-center justify-between px-4 py-4 border-t border-[#e5e4df]">
              <span className="text-[13px] text-[#68716d]">
                {totalFiltered > 0
                  ? `${startIndex + 1}–${Math.min(startIndex + 20, totalFiltered)} of ${totalFiltered}`
                  : '0'}{' '}
                {totalFiltered === 1 ? 'member' : 'members'}
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-[9px] border border-[#e6e9de] rounded-[9px] hover:bg-[#f6f6f3] transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-[13px] font-medium text-[#424a47]"
                >
                  Previous
                </button>
                <span className="px-4 py-[9px] text-[13px] font-medium text-[#424a47] min-w-[44px] text-center">
                  {page}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!data?.pagination || page >= data.pagination.totalPages}
                  className="px-4 py-[9px] border border-[#e6e9de] rounded-[9px] hover:bg-[#f6f6f3] transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-[13px] font-medium text-[#424a47]"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* View Member Modal */}
      {selectedMember && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedMember(null)}
        >
          <div
            className="bg-white rounded-[13px] shadow-[0_21px_45px_rgba(0,0,0,0.15)] p-8 max-w-[520px] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-[21px] font-semibold text-[#424a47]">Member Details</h2>
              <button
                onClick={() => setSelectedMember(null)}
                className="text-[#68716d] hover:text-[#424a47] text-[28px] font-light leading-none w-[32px] h-[32px] flex items-center justify-center"
              >
                ×
              </button>
            </div>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-[48px] h-[48px] rounded-full bg-[#e1e5eb] text-[#4b607f] flex items-center justify-center font-medium text-[15px]">
                {selectedMember.firstName[0]}
                {selectedMember.lastName[0]}
              </div>
              <div className="flex-1">
                <div className="font-semibold text-[#424a47] text-[16px]">
                  {selectedMember.firstName} {selectedMember.lastName}
                </div>
                <div className="text-[13px] text-[#68716d]">{selectedMember.email}</div>
              </div>
            </div>

            <dl className="space-y-4 pb-6 border-b border-[#e5e4df]">
              <div>
                <dt className="text-[11px] text-[#68716d] uppercase tracking-[0.5px] mb-1">
                  Treaty number
                </dt>
                <dd className="text-[14px] text-[#424a47] font-medium font-mono">
                  {selectedMember.treatyNumber}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] text-[#68716d] uppercase tracking-[0.5px] mb-1">
                  Phone
                </dt>
                <dd className="text-[14px] text-[#424a47]">
                  {selectedMember.phone || 'Not provided'}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] text-[#68716d] uppercase tracking-[0.5px] mb-1">
                  Status
                </dt>
                <dd>
                  <span
                    className={`px-[11px] py-[5px] rounded-full text-[11px] font-medium capitalize border ${
                      selectedMember.status === 'active'
                        ? 'bg-[#e1e5eb] text-[#4b607f] border-[#d1d8e3]'
                        : selectedMember.status === 'pending'
                          ? 'bg-[#fef8e6] text-[#b8942c] border-[#f4e6b8]'
                          : selectedMember.status === 'suspended'
                            ? 'bg-[#fff1e6] text-[#f3701e] border-[#f0d8c1]'
                            : 'bg-[#f6f6f3] text-[#68716d] border-[#e5e4df]'
                    }`}
                  >
                    {selectedMember.status}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-[11px] text-[#68716d] uppercase tracking-[0.5px] mb-1">
                  Registered
                </dt>
                <dd className="text-[14px] text-[#424a47]">
                  {new Date(selectedMember.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </dd>
              </div>
            </dl>

            <button
              onClick={() => setSelectedMember(null)}
              className="w-full mt-6 px-4 py-[11px] bg-[#242728] text-white rounded-[9px] text-[13px] font-semibold hover:bg-[#3c413d] active:scale-[0.98] transition-all"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Photo ID Modal */}
      {photoIdModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setPhotoIdModal(null)}
        >
          <div
            className="bg-white rounded-[13px] shadow-[0_21px_45px_rgba(0,0,0,0.15)] p-8 max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-[21px] font-semibold text-[#424a47] mb-1">Photo ID</h2>
                <p className="text-[13px] text-[#68716d]">SAS URL expires in 5 minutes</p>
              </div>
              <button
                onClick={() => setPhotoIdModal(null)}
                className="text-[#68716d] hover:text-[#424a47] text-[28px] font-light leading-none w-[32px] h-[32px] flex items-center justify-center"
              >
                ×
              </button>
            </div>

            <div className="flex justify-center bg-[#f6f6f3] rounded-[9px] p-4">
              <img
                src={photoIdModal.url}
                alt="Member Photo ID"
                className="max-w-full max-h-[70vh] object-contain"
              />
            </div>

            <button
              onClick={() => setPhotoIdModal(null)}
              className="w-full mt-6 px-4 py-[11px] bg-[#242728] text-white rounded-[9px] text-[13px] font-semibold hover:bg-[#3c413d] active:scale-[0.98] transition-all"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Reason Modal */}
      {showReasonModal && reasonModalData && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4"
          onClick={() => {
            setShowReasonModal(false);
            setReasonText('');
            setReasonModalData(null);
          }}
        >
          <div
            className="bg-white rounded-[13px] shadow-[0_21px_45px_rgba(0,0,0,0.15)] p-8 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6">
              <h2 className="text-[21px] font-semibold text-[#424a47] mb-1">
                Status Change Reason
              </h2>
              <p className="text-[13px] text-[#68716d]">
                Provide a reason for changing status to {reasonModalData.status}
              </p>
            </div>
            <div className="space-y-5">
              <div>
                <label
                  htmlFor="reason"
                  className="block text-[13px] font-medium text-[#424a47] mb-2"
                >
                  Reason <span className="text-[#f3701e]">*</span>
                </label>
                <textarea
                  id="reason"
                  value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  className="w-full px-4 py-[10px] text-[14px] border border-[#e1e6d8] rounded-[9px] bg-white text-[#424a47] placeholder:text-[#68716d] focus:outline focus:outline-2 focus:outline-[#aeb6bf] focus:outline-offset-2 resize-y"
                  rows={4}
                  placeholder="Enter reason for status change..."
                  required
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={submitStatusChange}
                  disabled={!reasonText.trim()}
                  className="flex-1 bg-[#242728] text-white py-[11px] rounded-[9px] text-[13px] font-semibold hover:bg-[#3c413d] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Update Status
                </button>
                <button
                  onClick={() => {
                    setShowReasonModal(false);
                    setReasonText('');
                    setReasonModalData(null);
                  }}
                  className="px-6 py-[11px] border border-[#e6e9de] rounded-[9px] hover:bg-[#f6f6f3] transition-colors text-[13px] font-medium text-[#424a47]"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

export default MembersPage;
